/**
 * The Overview tab: what this project is, who is on it, and where its data goes.
 *
 * Extracted from ProjectDetailPage on 2026-08-25.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import QuotationsCard from "./QuotationsCard";
import OrdersCard from "./OrdersCard";
import ProjectDocumentsCard from "./ProjectDocumentsCard";
import UserPicker from "../components/UserPicker";
import { isGlobalAdmin, rankInGroup } from "../auth/roles";
import { G, G_DARK, Badge, Field, Input, Select, Textarea,
         Btn, Card, Spinner, formatWhen } from "../components/Shared";
import { serverNow } from "../serverClock";
import { useDisplayTimezone } from "../portalConfig";

export function ProjectLogCard({ project }) {
  const zone = useDisplayTimezone();
  const [entries, setEntries] = useState([]);
  const [showAccess, setShowAccess] = useState(false);

  useEffect(() => {
    api.get(`/api/projects/${project.id}/log?include_access=${showAccess}`)
      .then(setEntries).catch(() => {});
  }, [project.id, project.s3_health_ok, showAccess]);

  return (
    <Card style={{ marginBottom: 20, padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Project log
        </h3>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
          <input type="checkbox" checked={showAccess} onChange={e => setShowAccess(e.target.checked)} />
          Show logs
        </label>
      </div>

      {entries.length === 0 ? (
        <p style={{ margin: 0, color: "#6b7280", fontSize: 12 }}>Nothing recorded yet.</p>
      ) : (
        /* Ten rows tall and scrolled, rather than the first eight and the rest
           silently absent. A log that quietly stops at eight is worse than a
           short one: it looks complete. */
        <div data-log-scroll style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          {entries.map(e => (
            <div key={e.id} data-log-row style={{
              display: "flex", gap: 10, fontSize: 12, padding: "6px 8px", borderRadius: 6,
              background: e.level === "warning" ? "rgba(217,119,6,0.08)" : "#f9fafb",
              boxShadow: e.level === "warning" ? "inset 3px 0 0 #b45309" : undefined,
              color: e.level === "warning" ? "#92400e" : "#4b5563",
            }}>
              <span style={{ color: "#6b7280", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                {formatWhen(e.created_at, { zone })}
              </span>
              {/* WHO. It was missing entirely: the row printed the time and the
                  detail, so "somebody opened the project" never said who, even
                  though every entry has carried an actor all along. */}
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, color: "#374151", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}
                    title={e.actor || "system"}>
                {e.actor || "system"}
              </span>
              {/* minWidth:0 and overflow-wrap are both load-bearing, and neither
                  is enough alone. A flex item defaults to min-width:auto, so it
                  cannot shrink below its content; the details here routinely
                  carry a full s3://bucket/prefix/ location with no spaces to
                  break at, which is content that does not shrink. The result was
                  not a clipped line — the background is painted on the parent, so
                  the text simply ran out of the yellow box and across the white
                  card. Same family as the fixed-layout table columns: the
                  overflow is painted, not clipped. */}
              <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                {e.level === "warning" && <span aria-hidden="true" style={{ marginRight: 4 }}>⚠</span>}
                {e.detail || e.action}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Where this project's data lives — and, apart from one field, nothing here is
// this project's to change any more.
//
// Bucket, region, endpoint and credentials moved to the group, so an operator
// editing them per project was editing a copy that the resolver would ignore
// the moment the group was configured (_s3_settings_for takes the group whole,
// never field by field). The form is gone; the paths it used to sit above are
// what people actually came here to read.
//
// The delivery subfolder stays editable because it names a folder *inside* this
// project's own prefix, which nothing at group level could sensibly decide.
function ProjectStorageCard({ project, canManageS3, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [prefix, setPrefix]   = useState(project.results_prefix || "");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    setCheckMsg(null);
    try {
      const r = await api.post(`/api/projects/${project.id}/s3-health-check`);
      // A warning can arrive on a perfectly reachable bucket — data in the
      // folder with no record behind it (written outside the portal, or left by
      // a deletion before deletion cleared the folder) is the case that
      // matters, and this is the only screen that surfaces it.
      const warning = (r.warnings || [])[0];
      setCheckMsg(!r.ok ? { ok: false, text: r.error || "Storage is not reachable." }
                        : warning ? { ok: false, text: warning }
                                  : { ok: true, text: "Storage is reachable." });
      onUpdated(await api.get(`/api/projects/${project.id}`));
    } catch (error) {
      setCheckMsg({ ok: false, text: error.message });
    } finally {
      setChecking(false);
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      onUpdated(await api.patch(`/api/projects/${project.id}/delivery-prefix`,
                                { results_prefix: prefix }));
      setEditing(false);
    } catch (error) {
      setErr(error.message);
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ label, value, mono }) => (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div data-break style={{ fontSize: 14, color: "#1a1a1a", fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}>
        {value || <span style={{ color: "#6b7280" }}>—</span>}
      </div>
    </div>
  );

  return (
    <Card style={{ gridColumn: "1/-1" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Storage</h3>
        {canManageS3 && project.s3_configured && !editing && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleCheck} disabled={checking}
              style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #9ca3af", background: "#fff", color: "#374151", cursor: checking ? "default" : "pointer", fontFamily: "inherit", minHeight: 24 }}>
              {checking ? "Checking…" : "Check now"}
            </button>
            <button onClick={() => setEditing(true)}
              style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #9ca3af", background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "inherit", minHeight: 24 }}>
              Delivery subfolder
            </button>
          </div>
        )}
      </div>

      {checkMsg && (
        <div role="status" style={{
          margin: "10px 0 0", padding: "8px 12px", borderRadius: 8, fontSize: 12,
          background: checkMsg.ok ? "rgba(0,150,64,0.08)" : "rgba(217,119,6,0.10)",
          border: `1px solid ${checkMsg.ok ? "rgba(0,150,64,0.35)" : "rgba(217,119,6,0.45)"}`,
          color: checkMsg.ok ? "#00551f" : "#92400e",
        }}>
          {checkMsg.text}
        </div>
      )}

      {editing ? (
        <form onSubmit={handleSave} style={{ marginTop: 12 }}>
          {err && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: "#b91c1c" }}>{err}</div>}
          <Field label="Delivery subfolder"
                 hint={`Inside ${project.s3_prefix_base || "this project's folder"}. Blank uses the default.`}>
            <Input value={prefix} onChange={e => setPrefix(e.target.value)} placeholder="results" />
          </Field>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Btn type="button" variant="secondary"
                 onClick={() => { setEditing(false); setPrefix(project.results_prefix || ""); setErr(null); }}>
              Cancel
            </Btn>
            <Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
          </div>
        </form>
      ) : project.s3_configured ? (
        <>
          <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            <Row label="Bucket" value={project.s3_bucket} mono />
            <Row label="Region" value={project.s3_region} mono />
            <Row label="Prefix" value={project.s3_prefix_base} mono />
            <Row label="Delivery Subfolder" value={project.results_prefix_effective} mono />
            {project.s3_endpoint_url && <Row label="Endpoint URL" value={project.s3_endpoint_url} mono />}
          </div>
          {/* s3_source has always been returned and never shown. Without it the
              page cannot answer the first question an operator asks when a
              bucket looks wrong: whose setting is this? */}
          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            {project.s3_source === "group"
              ? "Configured by the group, under Management → Facility settings → Storage management."
              : project.s3_source === "project"
                ? "Configured on this project itself, before storage moved to the group."
                : null}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: "#6b7280", padding: "10px 0" }}>
          No storage configured for this project's group — a group admin sets it up
          under Management → Facility settings → Storage management.
          {project.group_operator_email && (
            <> <a href={`mailto:${project.group_operator_email}`} style={{ color: G_DARK }}>{project.group_operator_email}</a></>
          )}
        </div>
      )}
    </Card>
  );
}

// Rendered straight from `project` rather than from a second fetch of its own.
// That is what makes it correct the moment the S3 config is saved: the parent
// already replaces `project` with the server's response, so there is no
// separately-loaded copy left to go stale.
function DeliveryS3Card({ project, canManage }) {
  if (!canManage) return null;

  const Row = ({ label, value, mono }) => (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div data-break style={{ fontSize: 14, color: "#1a1a1a", fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}>
        {value || <span style={{ color: "#6b7280" }}>—</span>}
      </div>
    </div>
  );

  return (
    <Card style={{ gridColumn: "1/-1" }}>
      <div style={{ marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>S3 Delivery Destination</h3>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
          Same bucket and project path as the S3 upload above, under the delivery subfolder.
        </p>
      </div>
      {!project.s3_configured ? (
        <div style={{ marginTop: 12, fontSize: 13, color: "#6b7280" }}>
          No S3 destination is configured for this project yet, so deliveries cannot be packaged.
        </div>
      ) : (
        <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px", marginTop: 8 }}>
          <Row label="Bucket" value={project.s3_bucket} mono />
          <Row label="Region" value={project.s3_region} mono />
          <Row label="Delivery source (put files here)" value={project.results_source_path} mono />
          <Row label="Packaged archives" value={project.results_archive_path} mono />
          <Row label="Endpoint URL" value={project.s3_endpoint_url} mono />
        </div>
      )}
    </Card>
  );
}

function ProjectDetailsCard({ project, canManage, onUpdated }) {
  // The group's own fields, and this project's tags. Both are decided by the
  // facility running the project, so an empty card here means that facility
  // has not defined any — not that something failed to load.
  const [values, setValues] = useState({});
  const [tagIds, setTagIds] = useState((project.tags || []).map(t => t.id));
  const [available, setAvailable] = useState([]);
  const [busy, setBusy]   = useState(false);
  // "saved" | "unchanged" | null — what the last click on Save details did.
  // "Saved." only once the PUT has answered: on dev a value the browser never
  // reported was "Saved." without a request ever being sent.
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  const fields = project.fields || [];

  useEffect(() => {
    setValues(Object.fromEntries(fields.map(f => [f.key, f.value ?? ""])));
    setTagIds((project.tags || []).map(t => t.id));
    setSaved(null);
    if (project.group_id) {
      api.get(`/api/groups/${project.group_id}/tags`).then(setAvailable).catch(() => setAvailable([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, project.group_id]);

  if (!project.group_id || (fields.length === 0 && available.length === 0)) return null;

  const saveFields = async () => {
    // Only what changed. Sending the whole set would let one stale form
    // overwrite somebody else's edit to a field it never showed.
    const changed = {};
    fields.forEach(f => {
      if ((f.value ?? "") !== (values[f.key] ?? "")) changed[f.key] = values[f.key] || null;
    });
    setError(null);
    if (!Object.keys(changed).length) { setSaved("unchanged"); return; }
    setBusy(true); setSaved(null);
    try {
      onUpdated(await api.put(`/api/projects/${project.id}/fields`, { values: changed }));
      setSaved("saved");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const toggleTag = async id => {
    const next = tagIds.includes(id) ? tagIds.filter(t => t !== id) : [...tagIds, id];
    setTagIds(next);
    setBusy(true); setError(null);
    try {
      onUpdated(await api.put(`/api/projects/${project.id}/tags`, { tag_ids: next }));
    } catch (e) { setError(e.message); setTagIds(tagIds); } finally { setBusy(false); }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      {/* Not "Project details" — that is the card above, and two cards with the
          same name on one screen is its own kind of bug. This one holds what the
          facility defined for its own projects. */}
      <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Additional details</h3>

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}

      {available.length > 0 && (
        <div style={{ marginBottom: fields.length ? 18 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Tags</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {available.map(t => {
              const on = tagIds.includes(t.id);
              return canManage ? (
                <button key={t.id} onClick={() => toggleTag(t.id)} disabled={busy}
                  aria-pressed={on}
                  style={{ border: on ? "1px solid transparent" : "1px dashed #d1d5db",
                           background: on ? "rgba(0,150,64,0.10)" : "#fff",
                           color: on ? "#006630" : "#6b7280",
                           borderRadius: 999, padding: "4px 12px", fontSize: 12,
                           cursor: busy ? "default" : "pointer", fontFamily: "inherit" }}>
                  {t.name}
                </button>
              ) : on ? <Badge key={t.id} color={t.colour || "blue"}>{t.name}</Badge> : null;
            })}
          </div>
        </div>
      )}

      {fields.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {fields.map(f => (
              <Field key={f.key} label={f.label} required={f.required} hint={f.help_text || undefined}>
                {f.type === "longtext" ? (
                  <Textarea rows={3} value={values[f.key] ?? ""} disabled={!f.is_active}
                            onChange={e => { setValues(v => ({ ...v, [f.key]: e.target.value })); setSaved(null); }} />
                ) : f.type === "choice" ? (
                  <Select options={(f.options || []).map(o => ({ value: o, label: o }))}
                          value={values[f.key] ?? ""} disabled={!f.is_active}
                          onChange={e => { setValues(v => ({ ...v, [f.key]: e.target.value })); setSaved(null); }} />
                ) : f.type === "boolean" ? (
                  <Select options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                          value={values[f.key] ?? ""} disabled={!f.is_active}
                          onChange={e => { setValues(v => ({ ...v, [f.key]: e.target.value })); setSaved(null); }} />
                ) : (
                  // A number field is a text input: a type="number" input reports
                  // "" for anything the browser cannot parse (a space, a locale
                  // comma), so the value vanished unsent. The server's Decimal
                  // check is the validation, and its refusal names the field.
                  <Input type={f.type === "date" ? "date" : "text"}
                         inputMode={f.type === "number" ? "decimal" : undefined}
                         value={values[f.key] ?? ""} disabled={!f.is_active}
                         onChange={e => { setValues(v => ({ ...v, [f.key]: e.target.value })); setSaved(null); }} />
                )}
              </Field>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
            <Btn onClick={saveFields} disabled={busy}>
              {busy ? <><Spinner /> Saving…</> : "Save details"}
            </Btn>
            {saved === "saved" && <span style={{ color: G_DARK, fontSize: 13 }}>Saved.</span>}
            {saved === "unchanged" && <span style={{ color: "#6b7280", fontSize: 13 }}>Nothing to save.</span>}
          </div>
        </>
      )}
    </Card>
  );
}

function MembersCard({ project, canManage, onUpdated }) {
  const [members, setMembers]   = useState([]);
  const [groupUsers, setGroupUsers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [newOwnerId, setNewOwnerId]     = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/projects/${project.id}/members`)
      .then(data => { setMembers(data); setErr(null); })
      .catch(error => setErr(error.message))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!canManage) return;
    api.get("/api/admin/users").then(setGroupUsers).catch(() => {});
  }, [canManage]);

  // No group condition here, on purpose. A researcher belongs to no group since
  // 2026-09-28 — they reach work only through the projects they are assigned
  // to — so matching `u.group_id` against the project's group hid every
  // researcher from every grouped project, which is exactly whom this card
  // exists to assign (reported 2026-10-30: a new account could not be added
  // anywhere). `/api/admin/users` is already scoped to whom this caller may
  // assign — unaffiliated accounts, their own groups' staff, everyone for an
  // admin — and `add_project_member` applies the same rule, so this only drops
  // what the server would refuse anyway: a deactivated or unconfirmed account,
  // and somebody who is already a member.
  const availableUsers = groupUsers.filter(
    u => u.is_active && (u.profile_state || "confirmed") === "confirmed" &&
         !members.some(m => m.user_id === u.id));
  const unconfirmedCount = groupUsers.filter(
    u => u.is_active && (u.profile_state || "confirmed") !== "confirmed" &&
         !members.some(m => m.user_id === u.id)).length;

  const handleAdd = async e => {
    e.preventDefault();
    if (!selectedUserId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/projects/${project.id}/members`, { user_id: Number(selectedUserId) });
      setSelectedUserId("");
      setAdding(false);
      load();
    } catch (error) {
      setErr(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async userId => {
    if (!window.confirm("Remove this user's access to the project?")) return;
    try {
      await api.delete(`/api/projects/${project.id}/members/${userId}`);
      load();
    } catch (error) {
      setErr(error.message);
    }
  };

  const handleTransfer = async e => {
    e.preventDefault();
    if (!newOwnerId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/projects/${project.id}/transfer-ownership`, { new_owner_id: Number(newOwnerId) });
      setNewOwnerId("");
      setTransferring(false);
      load();
      // The page's onUpdated is setProject: called with nothing, it set the
      // project to undefined and the page went blank. Hand it what the server
      // now holds — the owner changed, and so did who may do what here.
      onUpdated(await api.get(`/api/projects/${project.id}`));
    } catch (error) {
      setErr(error.message);
    } finally {
      setBusy(false);
    }
  };

  // Exactly what the backend accepts, and no more — a picker offering a name
  // the endpoint then refuses is the same defect as one hiding a name it would
  // have taken. Admins are eligible for any project, since they see every group
  // anyway; anyone else needs the project to *have* a group and to be in it.
  const ownerCandidates = groupUsers.filter(
    u => u.is_active && u.id !== project.created_by &&
         (isGlobalAdmin(u) || rankInGroup(u, project.group_id) !== null),
  );

  return (
    <Card style={{ gridColumn: "1/-1" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: "#374151" }}>
          <span style={{ color: "#6b7280" }}>Owner: </span>
          {project.owner_email
            ? <strong>{project.owner_name || project.owner_email}</strong>
            : <span style={{ color: "#6b7280", fontStyle: "italic" }}>none — the previous owner's account was removed</span>}
        </div>
        {canManage && !transferring && (
          <button onClick={() => setTransferring(true)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>
            Transfer ownership
          </button>
        )}
      </div>

      {transferring && (
        <form onSubmit={handleTransfer} style={{ marginBottom: 16 }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>
            The new owner gets the access the current one has. This is also how you free up an
            account for deletion — deleting the project instead throws away every record of the
            work done on it.
          </p>
          {/* An empty picker beside a disabled button explains nothing, which is
              how this was reported in the first place: "I can only transfer it
              to myself". Whatever narrows the list has to say so on screen. */}
          {!project.group_id && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#92400e" }}>
              This project is not in a group, so only an admin can own it — anyone else would hold
              a project no operator can see. Set a group under Project Details first, and then it
              can go to any member of that group.
            </p>
          )}
          {ownerCandidates.length === 0 && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#92400e" }}>
              There is nobody else this project can be transferred to yet.
            </p>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <Select
                options={ownerCandidates.map(u => ({ value: u.id, label: u.full_name ? `${u.full_name} (${u.email})` : u.email }))}
                value={newOwnerId}
                onChange={e => setNewOwnerId(e.target.value)}
              />
            </div>
            <Btn type="submit" disabled={busy || !newOwnerId}>{busy ? "Transferring…" : "Transfer"}</Btn>
            <Btn variant="secondary" onClick={() => { setTransferring(false); setNewOwnerId(""); setErr(null); }}>Cancel</Btn>
          </div>
        </form>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Assigned Users</h3>
        {canManage && !adding && (
          <button onClick={() => setAdding(true)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>
            + Assign
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={handleAdd} style={{ marginTop: 12, marginBottom: 12 }}>
          {availableUsers.length === 0 && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#92400e" }}>
              Everyone you can assign already has access to this project.
            </p>
          )}
          {unconfirmedCount > 0 && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>
              {unconfirmedCount === 1 ? "One account is" : `${unconfirmedCount} accounts are`} not
              listed because the facility has not confirmed {unconfirmedCount === 1 ? "it" : "them"} yet —
              confirm them in Management → Users first.
            </p>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <Select
                options={availableUsers.map(u => ({ value: u.id, label: u.full_name ? `${u.full_name} (${u.email})` : u.email }))}
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
              />
            </div>
            <Btn type="submit" disabled={busy || !selectedUserId}>{busy ? "Adding…" : "Add"}</Btn>
            <Btn variant="secondary" onClick={() => { setAdding(false); setSelectedUserId(""); setErr(null); }}>Cancel</Btn>
          </div>
        </form>
      )}

      {err && <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}>{err}</div>}

      {loading ? (
        <div style={{ fontSize: 13, color: "#6b7280", padding: "10px 0" }}>Loading…</div>
      ) : members.length === 0 ? (
        <div style={{ fontSize: 13, color: "#6b7280", padding: "10px 0" }}>
          Only the project creator has access. {canManage && "Assign researchers or staff above."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {members.map(m => (
            <div key={m.id} data-member-row style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
              <div>
                <span style={{ fontSize: 13, color: "#1a1a1a" }}>{m.full_name || m.email}</span>
                {m.full_name && m.email && <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 6 }}>{m.email}</span>}
                {m.user_id === project.representative_user_id && (
                  <span title="Change the representative under Project Details to remove this member"
                        style={{ fontSize: 11, color: "#1e40af", background: "#dbeafe", borderRadius: 4, padding: "1px 6px", marginLeft: 8 }}>
                    representative
                  </span>
                )}
              </div>
              {/* The representative is a member for as long as they represent
                  the project, and the backend refuses to remove them — so no ×
                  that could only fail. Change the representative first. */}
              {canManage && m.user_id !== project.representative_user_id && (
                <button onClick={() => handleRemove(m.user_id)} aria-label={`Remove ${m.full_name || m.email}`} title="Remove" style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── The project's three dates ────────────────────────────────────────────────
//
// Created > quote agreed > closing on. The card showed only the first of them,
// which said when the paperwork started and nothing about how long anything had
// left — the two numbers people actually came to the page for.
//
// Every count is rendered FORWARDS and never as a minus sign, the same rule the
// quotation rows keep: "-3 days" makes the reader do the subtraction and hides
// the word they act on.

const DATE_ASIDE = { marginLeft: 8, fontSize: 12, color: "#6b7280" };
const ABSENT = { color: "#6b7280" };

// How long ago, against the SERVER's clock. Not `Date.now()`: the deadlines on
// this page are decided server-side, and a count read off the viewer's laptop
// can be a day out from the one the nightly sweep is working to.
function sinceText(value) {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((serverNow() - then) / 86400000);
  if (days < 0) return null;                 // clock skew — say nothing rather than "-1 days ago"
  if (days === 0) return "today";
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// The backend's `project_days_left` where there is one, and only otherwise our
// own arithmetic. It comes from `deps.project_deadline`, which is the single
// derivation the nightly archive sweep runs on — a second rounding here would
// have the screen and the sweep disagreeing by a day about the same project.
function leftText(daysLeft, when) {
  const n = daysLeft != null ? daysLeft
    : Math.floor((new Date(when).getTime() - serverNow()) / 86400000);
  if (Number.isNaN(n)) return null;
  if (n > 0) return `${n} day${n === 1 ? "" : "s"} left`;
  if (n === 0) return "today";
  const over = Math.abs(n);
  // Not "overdue": reaching this date does not mean the project is late or
  // archived — the sweep waits for its last live quotation. It has simply
  // passed.
  return `passed ${over} day${over === 1 ? "" : "s"} ago`;
}

/**
 * One date, with what it means in days beside it — or the reason there isn't one.
 *
 * **An absent date and an unknown date are different facts**, and `absent` is
 * where the caller says which. A project with no accepted quotation HAS no
 * "quote agreed" date and a dash cannot say so; a response that simply did not
 * carry the field is a different problem with a different fix (reload), and one
 * the schema warns about by name — four route groups once answered `ProjectOut`
 * from a bare `_project_out`, so adding a single tag blanked the closing date
 * on this card until a reload (fixed 2026-09-16).
 */
function ProjectDate({ when, zone, ahead = false, daysLeft = null, absent }) {
  if (!when) return <span style={ABSENT}>{absent}</span>;
  const aside = ahead ? leftText(daysLeft, when) : sinceText(when);
  return (
    <>
      {formatWhen(when, { zone })}
      {aside && <span style={DATE_ASIDE}>{aside}</span>}
    </>
  );
}

export default function OverviewTab({ project, isAdmin, canManageS3, onUpdated }) {
  const zone = useDisplayTimezone();
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({
    affiliation_id: project.affiliation_id ?? "",
    representative_user_id: project.representative_user_id ?? null,
    group_id: project.group_id ?? "",
    notes: project.notes || "",
  });
  // What the picker shows, not what is sent. For a project whose representative
  // account has since been deleted this is the text the project kept — the id
  // is NULL and the name is all there is.
  const [repLabel, setRepLabel] = useState(project.representative_name || "");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [groups, setGroups]   = useState([]);
  // Affiliation belongs here, with the project's other standing facts, rather
  // than in the card of group-defined extras where it first landed. It decides
  // which price column applies — that is not an optional extra field.
  const [affiliations, setAffiliations] = useState([]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // The group decides which operators can see the project at all, so only an
  // admin is offered it — and only an admin can load the list anyway.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/api/admin/groups").then(setGroups).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!project.group_id) return;
    api.get(`/api/affiliations?group_id=${project.group_id}`)
      .then(setAffiliations).catch(() => setAffiliations([]));
  }, [project.group_id]);

  const handleSave = async e => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // A <select> hands back a string, and "" is not a number: sending it
      // straight through is a 422. Left out entirely rather than sent as null,
      // since PATCH drops nulls and a project cannot be taken *out* of a group.
      const payload = { ...form };
      if (payload.group_id === "") delete payload.group_id;
      else payload.group_id = Number(payload.group_id);
      if (payload.affiliation_id === "") delete payload.affiliation_id;
      else payload.affiliation_id = Number(payload.affiliation_id);
      // Left out rather than sent as null, but only for a project that opened
      // without one: the server refuses an explicit null — a project cannot be
      // left without a representative — and this form opens holding null on
      // every project from before that rule, so sending it would make Save fail
      // on exactly those. When the project *had* a representative and the null
      // is what the operator just did to it, the null is the edit and has to go
      // — stripping it there turns the server's deliberate 422 into a 200 that
      // changed nothing, and the operator believes they took somebody off a
      // project they are still on.
      if (payload.representative_user_id == null && project.representative_user_id == null)
        delete payload.representative_user_id;
      const updated = await api.patch(`/api/projects/${project.id}`, payload);
      onUpdated(updated);
      setEditing(false);
    } catch (error) {
      setErr(error.message);
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ label, value, mono }) => (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div data-break style={{ fontSize: 14, color: "#1a1a1a", fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}>
        {value || <span style={{ color: "#6b7280" }}>—</span>}
      </div>
    </div>
  );

  return (
    <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Project Details</h3>
          {/* canManageS3 is the project-level manager check. It used to be
              isAdmin, which left an operator unable to correct the notes or the
              representative on a project they run. */}
          {canManageS3 && !editing && (
            <button onClick={() => setEditing(true)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
            {/* Data Type and Organism used to sit here. They are group-defined
                fields now — see the Project details card — so a facility that
                wants them defines them and every other one is not asked. */}
            {affiliations.length > 0 && (
              <Field label="Affiliation" required
                     hint="Which price column applies to this customer. Without one the project cannot be quoted.">
                <Select options={[{ value: "", label: "— choose one —" },
                                  ...affiliations.map(a => ({ value: String(a.id), label: a.label || a.code }))]}
                        value={String(form.affiliation_id ?? "")}
                        onChange={set("affiliation_id")} />
              </Field>
            )}
            <Field label="Project representative" required
                   hint="Start typing a name. It has to be somebody with an account here — the name and the address on the project are then that account's.">
              <UserPicker
                value={form.representative_user_id ?? null}
                valueLabel={repLabel}
                onChange={person => {
                  // The address used to be editable beside this, which meant a
                  // project could name one person and write to another. It is
                  // the account's now, and only the id is sent.
                  setRepLabel(person ? (person.full_name || person.email) : "");
                  setForm(f => ({ ...f, representative_user_id: person ? person.id : null }));
                }}
              />
            </Field>
            {/* Admins only: moving a project between groups moves it out of the
                sight of the operators looking after it, and the server refuses
                it to anybody else. */}
            {isAdmin && (
              <Field label="Group" hint="Decides which operators can see this project, and who it can be handed to. A project with no group is visible only to admins and its owner.">
                <Select
                  options={groups.map(g => ({ value: g.id, label: g.name }))}
                  value={form.group_id}
                  onChange={set("group_id")}
                />
              </Field>
            )}
            <Field label="Notes">
              <Textarea rows={3} value={form.notes} onChange={set("notes")} />
            </Field>
            {err && <div style={{ fontSize: 12, color: "#dc2626" }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => { setEditing(false); setErr(null); }}>Cancel</Btn>
              <Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
            </div>
          </form>
        ) : (
          <>
            <Row label="Affiliation"
                 value={affiliations.find(a => a.id === project.affiliation_id)?.label
                        || affiliations.find(a => a.id === project.affiliation_id)?.code} />
            <Row label="Project representative"   value={project.representative_name} />
            <Row label="Representative email"  value={project.representative_email} mono />
            {project.group_name && <Row label="Group" value={project.group_name} />}
            {/* The gated chain, as far as it has got: created > quotation
                agreed > closing on. `order_started_at` is the day this project's FIRST
                quotation was signed — the column has been written at that
                moment since 2026-09-16, and it was in `_project_out`'s dict all
                along while missing from `ProjectOut`, so Pydantic dropped it
                and no screen could read it. */}
            <Row label="Project created"
                 value={<ProjectDate when={project.created_at} zone={zone} />} />
            {/* "Quotation agreed", not "Quote agreed": the card below this
                one calls the same thing a quotation throughout, and one thing
                under two words on one page is what this rename ends. */}
            <Row label="Quotation agreed"
                 value={<ProjectDate when={project.order_started_at} zone={zone}
                                     absent="No quotation has been accepted yet, so no work has started." />} />
            <Row label="Closing on"
                 value={<ProjectDate
                          when={project.project_deadline} zone={zone} ahead
                          daysLeft={project.project_days_left}
                          /* Two different absences, told apart by the days the
                             project was stamped with at creation: no stamp
                             means there is no closing date at all, while a
                             stamp with no date worked out means this response
                             did not compute it — a reload fixes the second and
                             nothing fixes the first.
                             **Zero is also "no closing date"**, and reading it
                             as a stamp printed "0 days from creation … reload
                             the project" for ever, because nothing was broken:
                             `deps.project_deadline` returns None for
                             `is None or <= 0`, which is the facility's decision
                             that a zero-day project simply has no clock. The
                             two tests must match that expression, not `null`
                             alone (2026-09-16). */
                          absent={project.project_due_days == null || project.project_due_days <= 0
                            ? "No closing date — nothing archives this project on a clock."
                            : `${project.project_due_days} days from creation, but the last response did not work the date out. Reload the project.`} />} />
            <Row label="Notes" value={project.notes} />
          </>
        )}
      </Card>
      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Activity</h3>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1, textAlign: "center", padding: "18px 12px", background: "#f9fafb", borderRadius: 10 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: G }}>{project.submission_count}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Submission{project.submission_count !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ flex: 1, textAlign: "center", padding: "18px 12px", background: "#f9fafb", borderRadius: 10 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: G }}>{project.result_count}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Result{project.result_count !== 1 ? "s" : ""}</div>
          </div>
        </div>
      </Card>
      <ProjectStorageCard project={project} canManageS3={canManageS3} onUpdated={onUpdated} />
      <DeliveryS3Card project={project} canManage={canManageS3} />
      <ProjectDocumentsCard project={project} onUpdated={onUpdated} />
      <ProjectDetailsCard project={project} canManage={canManageS3} onUpdated={onUpdated} />
      <DeadlinesCard project={project} />
      {/* The chain's own order: deadlines, money, the quotations themselves.
          Behind `canManage` — the same gate every card showing money sits
          behind today. The server would tell a member this much about their own
          project; the UI deliberately offers less until the facility says
          otherwise, the same call the card-scoped export made. */}
      {canManageS3 && <ProjectMoneyCard project={project} />}
      <QuotationsCard project={project} canManage={canManageS3} />
      {/* Directly under the quotations, because that is the sequence the chain runs
          in: a piece of work exists because a quotation was priced, and its
          clock starts when that quotation is signed. `canManage` is the same
          per-project question the server asks — `_manages_project`, which is
          what `canManageS3` already mirrors — and the card asks auth/roles.js
          the narrower group-admin question itself, for the Agree button. */}
      <OrdersCard project={project} canManage={canManageS3} onUpdated={onUpdated} />
      <MembersCard project={project} canManage={canManageS3} onUpdated={onUpdated} />
    </div>
  );
}

/**
 * The two deadlines, as they stand for this project — read, never written.
 *
 * Since 2026-09-14 both numbers come from the group and from nobody else: a
 * group admin or an admin sets them under Facility settings, and
 * `PATCH /api/projects/{id}/deadlines` no longer exists. The person filling in a
 * project form does not decide what eventually archives it, once or otherwise.
 *
 * A project that carries its own value from before that is still honoured, so
 * the source sentence stays and matters: "30 days (the facility's)" and
 * "30 days (this project's)" are the same figure and different situations — the
 * second one now being a value nobody can change, here or anywhere.
 */
function DeadlinesCard({ project }) {
  // `quote_valid_days` and `order_due_days` are the columns' names; the labels
  // are what the two numbers ARE, and they are **two different periods**: how
  // long there is to SIGN, and how long the WORK has once it is signed.
  //
  // Both were briefly called "Quotation …" on 2026-09-16, during the sweep that
  // took *order* and *offer* out of every screen. That removed the word and the
  // distinction with it — two rows starting with the same noun, for the two
  // halves of a quotation's life that a facility sets separately and for
  // different reasons. Only the word had to go.
  const ROWS = [
    { key: "quote_valid_days", label: "Quotation valid for",
      hint: "before it must be signed", own: project.quote_valid_days,
      effective: project.quote_valid_days_effective },
    { key: "order_due_days", label: "Work deadline",
      hint: "for the work, once it is signed", own: project.order_due_days,
      effective: project.order_due_days_effective },
  ];

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Deadlines</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Both are the facility's, set for the whole group under <strong>Facility settings</strong> by
        a group admin or an admin. They cannot be changed on a project — a few projects still carry
        a value of their own from before that rule, and those are fixed for everybody.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {ROWS.map(r => (
          <div key={r.key} style={{ display: "flex", gap: 10, alignItems: "center",
                                    flexWrap: "wrap", fontSize: 13 }}>
            {/* Both labels now begin with the same word, so the hint is not
                decoration: "valid for" and "deadline" are the same quotation
                before and after it is signed, and a reader who takes one for
                the other reads the wrong number. */}
            <span style={{ minWidth: 150, color: "#374151" }}>{r.label}</span>
            <span style={{ color: "#6b7280", fontSize: 12 }}>{r.hint}</span>
            <strong>
              {r.effective === null || r.effective === undefined
                ? "no deadline" : `${r.effective} days`}
            </strong>
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              {r.own ? "this project's, and fixed" : "the facility's"}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * What this project has earned, and what it still expects.
 *
 * Four figures, each net and gross, and **every one of them counted per
 * quotation** — `services/stats.order_money`. A project runs for years and
 * collects pieces of agreed work; pricing it from one accepted quotation, which
 * is what the Statistics page still does, reports the money of one of them and
 * buckets it on the project's single outcome, so *Delivered* and *Outstanding*
 * could never both be non-zero on one card. They can here, and this card and
 * that page will disagree on a project holding several until that report is
 * re-pointed at the same derivation.
 *
 * **Nothing here adds anything up.** Every figure is the string the API sent:
 * `services/quotations._money_str` sends Decimals out as strings precisely so
 * that no screen does arithmetic on them — 0.10 and 0.20 make
 * 0.30000000000000004 in JavaScript — and a total worked out on this card could
 * disagree with the PDF in the customer's hand by a cent for ever.
 *
 * **An absent figure and a figure of nothing are different facts**, the rule
 * `ProjectDate` above states for the three dates and this card keeps for the
 * money: a missing gross is what an affiliation with no VAT rate has, and nine
 * of thirteen on dev are in that state. Printed as 0.00 it would say the work
 * was free.
 *
 * One block per currency, never one number over both: the currency is stamped
 * per quotation from the group and a project can be moved between groups, so a
 * project holding two is legitimate and a single sum would be a figure in no
 * currency at all.
 */
const MONEY_FIGURES = [
  { key: "delivered",   label: "Delivered",   hint: "work that finished" },
  { key: "outstanding", label: "Outstanding", hint: "agreed and still running" },
  { key: "lapsed",      label: "Lapsed",      hint: "agreed, then cancelled or expired" },
  { key: "total",       label: "Total",       hint: "outstanding and delivered together" },
];

function MoneyFigure({ figure, currency }) {
  if (!figure) return null;
  return (
    <>
      <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        {figure.net} {currency}
      </strong>
      <span style={{ fontSize: 12, color: "#6b7280" }}>excluding VAT</span>
      {figure.gross == null ? (
        /* Not a zero and not the net figure repeated: this project's
           affiliation carries no VAT rate, so there IS no gross — the same
           distinction the dates above make between an absent value and one this
           response did not work out. */
        <span style={{ ...ABSENT, fontSize: 12 }}>no VAT rate, so no figure including VAT</span>
      ) : (
        <>
          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {figure.gross} {currency}
          </strong>
          <span style={{ fontSize: 12, color: "#6b7280" }}>including VAT</span>
        </>
      )}
    </>
  );
}

function ProjectMoneyCard({ project }) {
  // `null` is "this response did not work it out" and an empty list is "this
  // project has earned nothing yet". Two absences, two different fixes — a
  // reload settles the first and nothing settles the second — and the schema
  // warns about the first by name: four route groups once answered `ProjectOut`
  // from a bare `_project_out` and blanked the closing date on this very tab.
  const currencies = project.money_currencies;
  const unpriced = project.money_unpriced_orders || 0;

  return (
    <Card>
      <div data-money-card>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Money</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
          Counted per quotation, because a project collects them over its life.{" "}
          <strong>Delivered</strong> is work that finished — the record goes to the billing
          office, which writes the invoice from it.{" "}
          <strong>Outstanding</strong> is agreed and still running, <strong>Lapsed</strong> was
          agreed and then cancelled or expired. <strong>Total</strong> is outstanding and
          delivered; lapsed is deliberately not in it.
        </p>

        {!currencies ? (
          <p style={{ margin: 0, fontSize: 13, ...ABSENT }}>
            This response did not work the money out. Reload the project.
          </p>
        ) : currencies.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, ...ABSENT }}>
            Nothing priced yet — no quotation on this project has been sent or accepted.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {currencies.map(c => (
              <div key={c.currency} data-money-currency={c.currency}>
                {/* The currency is named once per block and again on every
                    figure: a reader who scrolls past the heading must not be
                    able to read a number as their own currency. */}
                <div style={{ fontSize: 12, fontWeight: 700, color: "#374151",
                              textTransform: "uppercase", letterSpacing: "0.06em",
                              marginBottom: 8 }}>
                  {c.currency}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {MONEY_FIGURES.map(f => (
                    <div key={f.key} data-money-row={f.key}
                         style={{ display: "flex", gap: 10, alignItems: "baseline",
                                  flexWrap: "wrap", fontSize: 13 }}>
                      <span style={{ minWidth: 110, color: "#374151" }}>{f.label}</span>
                      <span style={{ color: "#6b7280", fontSize: 12, minWidth: 190 }}>{f.hint}</span>
                      <MoneyFigure figure={c[f.key]} currency={c.currency} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {unpriced > 0 && (
          /* Stated rather than hidden. Work whose quotation carries no readable
             frozen figures is left out of every number above, and a figure that
             is quietly short looks complete. */
          <p style={{ margin: "16px 0 0", fontSize: 12, ...ABSENT }}>
            {unpriced} quotation{unpriced === 1 ? "" : "s"} could not be priced and{" "}
            {unpriced === 1 ? "is" : "are"} in none of the figures above.
          </p>
        )}
      </div>
    </Card>
  );
}

// ── Submissions tab ───────────────────────────────────────────────────────────

